import { getJSON, postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import type {
  BulkUpdateChargeByPayload,
  ChargeReconciliationFilters,
  ChargeReconciliationRow,
  UpdateChargeReconciliationPayload
} from '../types/chargeReconciliation'

export const CHARGE_RECONCILIATION_QUERY_KEY = 'charge-reconciliation'

export interface ChargeReconciliationListApiResponse {
  data: ChargeReconciliationRow[]
  pagination: { totalItems: number }
}

export const buildChargeReconciliationQueryString = (
  filters: ChargeReconciliationFilters
): string => {
  const params = new URLSearchParams()
  params.set('startTime', filters.startTime)
  params.set('endTime', filters.endTime)
  if (filters.status) {
    params.set('status', filters.status)
  }
  // Repeated `[]` params (not indexed) so the BE's qs parser keeps them as an
  // array regardless of count (indexed keys degrade to an object past ~20).
  filters.serviceUserIds?.forEach((id) => {
    params.append('serviceUserIds[]', id)
  })
  filters.fundingTypeIds?.forEach((id) => {
    params.append('fundingTypeIds[]', id)
  })
  filters.eventTypeIds?.forEach((id) => {
    params.append('eventTypeIds[]', id)
  })
  filters.eventStatuses?.forEach((s) => {
    params.append('eventStatuses[]', s)
  })
  filters.warnings?.forEach((w) => {
    params.append('warnings[]', w)
  })
  if (filters.page !== undefined && filters.pageSize !== undefined) {
    params.set('pagination[page]', String(Math.max(1, filters.page)))
    params.set('pagination[limit]', String(filters.pageSize))
  }
  // The BE takes the pay-standard sort[field]=1|-1; the charge filter carries
  // sortField + sortOrder ('asc'|'desc'), so normalise here.
  if (filters.sortField !== undefined && filters.sortOrder !== undefined) {
    params.set(
      `sort[${filters.sortField}]`,
      filters.sortOrder === 'asc' ? '1' : '-1'
    )
  } else {
    params.set('sort[date]', '-1')
  }
  return params.toString()
}

export const useGetChargeReconciliation = (
  filters: ChargeReconciliationFilters,
  enabled = true
) => {
  const qs = buildChargeReconciliationQueryString(filters)
  return useQuery<ChargeReconciliationListApiResponse>(
    [CHARGE_RECONCILIATION_QUERY_KEY, filters],
    () =>
      getJSON({
        url: `charge-reconciliation?${qs}`,
        isRota: true
      }),
    {
      enabled,
      staleTime: 60 * 1000,
      keepPreviousData: true
    }
  )
}

export const useUpdateChargeReconciliation = (onSuccess?: () => void) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation(
    ({
      rotaInstanceId,
      payload
    }: {
      rotaInstanceId: string
      payload: UpdateChargeReconciliationPayload
    }) =>
      postJSON({
        url: `charge-reconciliation/${rotaInstanceId}`,
        method: 'PUT',
        isRota: true,
        json: payload
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CHARGE_RECONCILIATION_QUERY_KEY])
        showToast({
          type: 'success',
          title: t('RECONCILIATION_TOAST_UPDATE_SUCCESS')
        })
        onSuccess?.()
      },
      onError: (error: unknown) =>
        showToast({
          type: 'error',
          title:
            (error as { message?: string })?.message ||
            t('RECONCILIATION_TOAST_UPDATE_ERROR')
        })
    }
  )
}

export const useBulkUpdateChargeBy = (onSuccess?: () => void) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation(
    (payload: BulkUpdateChargeByPayload) =>
      postJSON({
        url: 'charge-reconciliation/bulk',
        method: 'PUT',
        isRota: true,
        json: payload
      }),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([CHARGE_RECONCILIATION_QUERY_KEY])
        const count = variables.rotaInstanceIds.length
        showToast({
          type: 'success',
          title:
            count > 1
              ? t('RECONCILIATION_TOAST_BULK_SUCCESS_MANY', { count })
              : t('RECONCILIATION_TOAST_BULK_SUCCESS_ONE')
        })
        onSuccess?.()
      },
      onError: (error: unknown) =>
        showToast({
          type: 'error',
          title:
            (error as { message?: string })?.message ||
            t('RECONCILIATION_TOAST_BULK_ERROR')
        })
    }
  )
}
